/**
 * Multiplayer: commander name (persisted), connection to the signals net, room browser, join by
 * code, create room. Joining/creating sets `lobbyStore.room`, which routes to the LobbyRoom.
 */
import { isTheatreId, type MapSize, SCENARIO_IDS, THEATRES, type TheatreId } from '@friction/engine';
import { useEffect, useState } from 'react';
import { inviteCode } from '../../app/nav';
import { settingsStore, useSettings } from '../../app/settings';
import { useT } from '../../i18n';
import { theatreTitle } from '../../i18n/names';
import { lobbyStore, useLobby } from '../../net/lobby';
import { localizeServerText } from '../../net/serverText';
import { Button, Chip, cx, Field, Rule, Segmented, Select, TextInput } from '../common/controls';
import { Icon } from '../common/Icon';
import menu from '../menu/menu.module.css';
import { DEFAULT_THEATRE, mapOptions, mapSizeInfo } from './presets';
import { ScreenFrame } from './ScreenFrame';
import s from './screens.module.css';

const CODE_LEN = 5;

export function ErrorBanner() {
  const t = useT();
  const error = useLobby((l) => l.error);
  const code = useLobby((l) => l.errorCode);
  if (!error) return null;
  const text = localizeServerText(error, t, code);
  return (
    <div className={s.error} role="alert" title={text === error ? undefined : error}>
      <Icon name="close" />
      <span>{text}</span>
      <Button variant="ghost" size="sm" onClick={() => lobbyStore.getState().clearError()}>
        {t('ui.dismiss')}
      </Button>
    </div>
  );
}

/** An invite link is used once per page load (leaving the room must not rejoin it). */
const invite = { used: false };

export function MultiplayerScreen() {
  const t = useT();
  const name = useSettings((x) => x.playerName);
  const status = useLobby((l) => l.status);
  const rooms = useLobby((l) => l.rooms);
  const ping = useLobby((l) => l.ping);
  const lobby = lobbyStore.getState();
  const [code, setCode] = useState(inviteCode ?? '');
  const [roomName, setRoomName] = useState('');
  const [mapSize, setMapSize] = useState<MapSize>('medium');
  const [map, setMap] = useState<TheatreId | 'generated'>(DEFAULT_THEATRE);
  const sizes = mapSizeInfo(t);

  const online = status === 'online';
  const nameOk = name.trim().length >= 2;
  const defaultRoom = t('mp.roomName.default', { name: name.trim() });

  // An invite link joins its room once online (the commander still picks a name first).
  useEffect(() => {
    if (!online || !inviteCode || invite.used) return;
    invite.used = true;
    history.replaceState(null, '', location.pathname);
    lobbyStore.getState().joinRoom(inviteCode);
  }, [online]);

  // Keep the list fresh while the browser is open.
  useEffect(() => {
    if (!online) return;
    lobbyStore.getState().refreshRooms();
    const timer = window.setInterval(() => lobbyStore.getState().refreshRooms(), 5000);
    return () => clearInterval(timer);
  }, [online]);

  const connect = () => {
    if (!nameOk) return;
    void lobbyStore
      .getState()
      .connect(name.trim())
      .catch(() => {});
  };

  return (
    <ScreenFrame kicker={t('mp.kicker')} title={t('mp.title')}>
      <div className={s.stack}>
        <div className={s.connectBar}>
          <Field label={t('mp.commander')} hint={t('mp.commander.hint')}>
            <TextInput
              value={name}
              maxLength={24}
              placeholder={t('mp.commander.placeholder')}
              disabled={status !== 'offline'}
              onChange={(v) => settingsStore.getState().set({ playerName: v })}
              onKeyDown={(e) => e.key === 'Enter' && connect()}
            />
          </Field>
          <div className={s.netStatus}>
            <span className={cx(menu.led, online && menu.ledOn, status === 'connecting' && menu.ledAmber)} />
            {online
              ? ping !== null
                ? t('mp.status.onlinePing', { ping })
                : t('mp.status.online')
              : status === 'connecting'
                ? t('mp.status.connecting')
                : t('mp.status.offline')}
          </div>
          {status === 'offline' ? (
            <Button variant="primary" icon="signal" disabled={!nameOk} onClick={connect}>
              {t('mp.connect')}
            </Button>
          ) : (
            <Button variant="secondary" icon="exit" onClick={() => lobby.disconnect()}>
              {t('mp.disconnect')}
            </Button>
          )}
        </div>

        <ErrorBanner />

        <div className={s.cols}>
          <section className={s.section}>
            <div className={s.row}>
              <Rule className={s.spacer}>{t('mp.openRooms')}</Rule>
              <Button size="sm" icon="refresh" disabled={!online} onClick={() => lobby.refreshRooms()}>
                {t('mp.refresh')}
              </Button>
            </div>
            {!online ? (
              <div className={s.empty}>
                <Icon name="radio" size={34} />
                <div className={s.emptyTitle}>{t('mp.noLine.title')}</div>
                <div>{t('mp.noLine.text')}</div>
              </div>
            ) : rooms.length === 0 ? (
              <div className={s.empty}>
                <Icon name="map" size={34} />
                <div className={s.emptyTitle}>{t('mp.empty.title')}</div>
                <div>{t('mp.empty.text')}</div>
              </div>
            ) : (
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>{t('mp.col.code')}</th>
                    <th>{t('mp.col.room')}</th>
                    <th>{t('mp.col.players')}</th>
                    <th>{t('mp.col.theatre')}</th>
                    <th>{t('mp.col.state')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rooms.map((r) => {
                    const full = r.players >= r.maxPlayers;
                    return (
                      <tr key={r.code}>
                        <td className={s.code}>{r.code}</td>
                        <td>{r.name}</td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>
                          {r.players}/{r.maxPlayers}
                        </td>
                        <td>
                          {r.scenario && SCENARIO_IDS.includes(r.scenario)
                            ? t(`scn.${r.scenario}.title`)
                            : r.theatre && isTheatreId(r.theatre)
                              ? theatreTitle(THEATRES[r.theatre].name, t.locale)
                              : t(`mapSize.${r.mapSize}`)}
                        </td>
                        <td>
                          {r.state === 'lobby' ? (
                            <Chip tone="ok">{t('mp.state.lobby')}</Chip>
                          ) : r.state === 'running' ? (
                            <Chip tone="warn">{t('mp.state.running')}</Chip>
                          ) : (
                            <Chip>{t('mp.state.finished')}</Chip>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={full && r.state === 'lobby'}
                            onClick={() => lobby.joinRoom(r.code)}
                          >
                            {r.state === 'running' ? t('mp.rejoin') : t('mp.join')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          <aside className={s.stack}>
            <section className={s.section}>
              <Rule>{t('mp.joinByCode')}</Rule>
              <TextInput
                className={s.codeInput}
                value={code}
                maxLength={CODE_LEN}
                placeholder="·····"
                disabled={!online}
                onChange={(v) => setCode(v.toUpperCase().replace(/[^A-Z]/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && code.length === CODE_LEN && lobby.joinRoom(code)}
                aria-label={t('mp.code.aria')}
              />
              <Button
                block
                disabled={!online || code.length !== CODE_LEN}
                onClick={() => lobby.joinRoom(code)}
              >
                {t('mp.joinRoom')}
              </Button>
            </section>
            <section className={s.section}>
              <Rule>{t('mp.newRoom')}</Rule>
              <Field label={t('mp.roomName')}>
                <TextInput
                  value={roomName}
                  maxLength={40}
                  placeholder={nameOk ? defaultRoom : t('mp.roomName.placeholder')}
                  disabled={!online}
                  onChange={setRoomName}
                />
              </Field>
              <Field label={t('sp.map')}>
                <Select<TheatreId | 'generated'>
                  value={map}
                  disabled={!online}
                  onChange={setMap}
                  options={mapOptions(t)}
                />
              </Field>
              {map === 'generated' && (
                <Field label={t('mp.theatre')}>
                  <Segmented<MapSize>
                    block
                    value={mapSize}
                    disabled={!online}
                    onChange={setMapSize}
                    options={sizes.map((m) => ({
                      value: m.value,
                      label: m.label,
                      title: `${m.meta} · ${m.players}`,
                    }))}
                  />
                </Field>
              )}
              <Button
                variant="primary"
                block
                icon="flag"
                disabled={!online}
                onClick={() =>
                  lobby.createRoom({
                    name: roomName.trim() || defaultRoom,
                    ...(map === 'generated' ? { mapSize } : { theatre: map }),
                  })
                }
              >
                {t('mp.create')}
              </Button>
              <span className={s.note}>{t('mp.hostNote')}</span>
            </section>
          </aside>
        </div>
      </div>
    </ScreenFrame>
  );
}
